import { safeSetDoc, safeDeleteDoc } from '../utils';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, MapPin, Globe, Phone, Clock, Info, Check, Map } from 'lucide-react';
import { auth, db } from '../firebase';
import { doc, serverTimestamp } from 'firebase/firestore';
import { appId } from '../constants';
import { useTags } from '../hooks/useTags';
import { checkPlacesRateLimit } from '../utils';
import { refreshRecommendations } from '../hooks/useRecommendations';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const MapSearchBox = ({ onPlaceSelected, onClearSearch, recommendations = EMPTY_ARRAY, aliases = EMPTY_ARRAY, authUser, className = "absolute top-4 left-4 z-[500]", overlayMode = false, externalPlaceId = null, dropdownAlign = 'left', minimal = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [customNote, setCustomNote] = useState('');
  const [happyHourNote, setHappyHourNote] = useState('');
  const [aliasName, setAliasName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [focusedPredictionIndex, setFocusedPredictionIndex] = useState(-1);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [tagsExpandedPath, setTagsExpandedPath] = useState([]);
  const [showWantToGoPrompt, setShowWantToGoPrompt] = useState(false);
  
  const containerRef = useRef(null);

  const onPlaceSelectedRef = useRef(onPlaceSelected);
  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  const { tagsHierarchy, availableTags, getParentTags, getChildTags } = useTags();



  const placesLibraryRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const refreshSessionToken = () => {
    if (placesLibraryRef.current?.AutocompleteSessionToken) {
      sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
    }
  };

  useEffect(() => {
    const initServices = async () => {
      if (window.google?.maps) {
        try {
          if (!window.google.maps.places) {
            placesLibraryRef.current = await window.google.maps.importLibrary('places');
          } else {
            placesLibraryRef.current = window.google.maps.places;
          }
          refreshSessionToken();
        } catch (e) {
          console.error('[MapSearchBox] Failed to load Google Places:', e);
        }
      }
    };
    initServices();
  }, []);

  const loadPlaceDetails = useCallback(async (placeId, fallbackSuggestion = null) => {
    if (!placesLibraryRef.current) return;
    try {
      setIsSearching(true);
      const place = new placesLibraryRef.current.Place({ id: placeId });
      
      if (!checkPlacesRateLimit()) {
        console.warn('[MapSearchBox] Rate limit hit for fetchFields.');
        throw new Error('Rate limit hit');
      }

      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'types', 'nationalPhoneNumber', 'websiteURI', 'regularOpeningHours', 'editorialSummary'],
        sessionToken: sessionTokenRef.current
      });

      const placeData = {
        placeId: place.id || placeId,
        name: place.displayName,
        formatted_address: place.formattedAddress,
        types: place.types,
        formatted_phone_number: place.nationalPhoneNumber,
        website: place.websiteURI,
        summary: place.editorialSummary,
        isOpenNow: place.regularOpeningHours?.isOpen ? place.regularOpeningHours.isOpen() : null,
        weekdayDescriptions: place.regularOpeningHours?.weekdayDescriptions || [],
        lat: place.location.lat(),
        lng: place.location.lng()
      };

      setSelectedPlace(placeData);
      setInputValue(placeData.name);
      setPredictions([]);
      setFocusedPredictionIndex(-1);
      
      const existingForNewPlace = recommendations.find(r => r.placeId === placeData.placeId && r.userId === auth.currentUser?.uid);
      if (existingForNewPlace) {
        const tags = existingForNewPlace.tags || [];
        setSelectedTags(tags);
        setCustomNote(existingForNewPlace.note || '');
        setHappyHourNote(existingForNewPlace.happyHourNote || '');

        const findPath = (node, currentPath) => {
            for (const [key, children] of Object.entries(node)) {
                if (tags.includes(key)) return [...currentPath, key];
                if (Object.keys(children).length > 0) {
                    const res = findPath(children, [...currentPath, key]);
                    if (res) return res;
                }
            }
            return null;
        };
        
        const path = findPath(tagsHierarchy, []);
        if (path && path.length > 0) {
            let node = tagsHierarchy;
            for (let i = 0; i < path.length; i++) node = node[path[i]] || {};
            if (Object.keys(node).length > 0) setTagsExpandedPath(path);
            else setTagsExpandedPath(path.slice(0, -1));
        } else {
            setTagsExpandedPath([]);
        }
      } else {
        setSelectedTags([]);
        setCustomNote('');
        setHappyHourNote('');
        setTagsExpandedPath([]);
      }
      
      const existingAlias = aliases.find(a => a.placeId === placeData.placeId && a.userId === auth.currentUser?.uid);
      if (existingAlias) {
        setAliasName(existingAlias.alias || '');
      } else {
        setAliasName('');
      }
      
      if (onPlaceSelectedRef.current) {
        onPlaceSelectedRef.current({
          lat: place.location.lat(),
          lng: place.location.lng(),
          place: placeData
        });
      }
      refreshSessionToken();
    } catch (e) {
      console.error('[MapSearchBox] Error loading place details:', e);
      // Fallback: If Google Places fails (e.g. custom placeId), load from DB
      const existingPlace = recommendations.find(r => r.placeId === placeId && r.userId === auth.currentUser?.uid);
      if (existingPlace) {
        setSelectedPlace({
          placeId: existingPlace.placeId,
          name: existingPlace.placeName || 'Unknown Place',
          formatted_address: existingPlace.address,
          lat: existingPlace.lat,
          lng: existingPlace.lng,
          types: [],
          weekdayDescriptions: existingPlace.weekdayDescriptions || []
        });
        setInputValue(existingPlace.placeName || '');
        setPredictions([]);
        setFocusedPredictionIndex(-1);
        
        const tags = existingPlace.tags || [];
        setSelectedTags(tags);
        setCustomNote(existingPlace.note || '');
        setHappyHourNote(existingPlace.happyHourNote || '');

        const existingAlias = aliases.find(a => a.placeId === placeId && a.userId === auth.currentUser?.uid);
        if (existingAlias) {
          setAliasName(existingAlias.alias || '');
        } else {
          setAliasName('');
        }

        const findPath = (node, currentPath) => {
            for (const [key, children] of Object.entries(node)) {
                if (tags.includes(key)) return [...currentPath, key];
                if (Object.keys(children).length > 0) {
                    const res = findPath(children, [...currentPath, key]);
                    if (res) return res;
                }
            }
            return null;
        };
        const path = findPath(tagsHierarchy, []);
        if (path && path.length > 0) {
            let node = tagsHierarchy;
            for (let i = 0; i < path.length; i++) node = node[path[i]] || {};
            if (Object.keys(node).length > 0) setTagsExpandedPath(path);
            else setTagsExpandedPath(path.slice(0, -1));
        } else {
            setTagsExpandedPath([]);
        }
      } else {
        // Clear state if neither API nor DB has the place
        setSelectedPlace(null);
        setSelectedTags([]);
        setCustomNote('');
        setHappyHourNote('');
        setAliasName('');
        setTagsExpandedPath([]);
        if (onClearSearch) onClearSearch();
      }
    } finally {
      setIsSearching(false);
    }
  }, [recommendations, aliases, tagsHierarchy, onClearSearch]);

  useEffect(() => {
    if (externalPlaceId && placesLibraryRef.current) {
        loadPlaceDetails(externalPlaceId);
    }
  }, [externalPlaceId, loadPlaceDetails]);

  const performSearch = (value) => {
    if (value.trim().length < 3) return;
    if (placesLibraryRef.current?.AutocompleteSuggestion) {
      setIsSearching(true);
      if (!sessionTokenRef.current) refreshSessionToken();
      
      if (!checkPlacesRateLimit()) {
        console.warn('[MapSearchBox] Rate limit hit.');
        setIsSearching(false);
        return;
      }

      placesLibraryRef.current.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: value,
        sessionToken: sessionTokenRef.current
      })
      .then(({ suggestions }) => {
        setIsSearching(false);
        if (suggestions && suggestions.length > 0) {
          setPredictions(suggestions);
        } else {
          setPredictions([]);
        }
      })
      .catch((err) => {
        console.error('[MapSearchBox] Autocomplete error:', err);
        setIsSearching(false);
      });
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setFocusedPredictionIndex(-1);
    
    if (value.trim().length < 3) {
      setPredictions([]);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 1000);
  };

  const handlePredictionSelect = (suggestion) => {
    if (!suggestion) return;
    const placeId = suggestion.placePrediction.placeId;
    loadPlaceDetails(placeId, suggestion);
  };

  const handleClear = useCallback(() => {
    // We intentionally do not clear inputValue so the user can easily edit their search
    setPredictions([]);
    setFocusedPredictionIndex(-1);
    setSelectedPlace(null);
    setSelectedTags([]);
    setCustomNote('');
    setHappyHourNote('');
    setAliasName('');
    setSaveSuccess(false);
    setTagSearchQuery('');
    setTagsExpandedPath([]);
    refreshSessionToken();
    if (onClearSearch) onClearSearch();
  }, [onClearSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedPlace && containerRef.current && !containerRef.current.contains(event.target)) {
        if (event.target.closest && (event.target.closest('.leaflet-marker-icon') || event.target.closest('.leaflet-popup') || event.target.closest('.leaflet-control'))) {
          return;
        }
        setInputValue('');
        handleClear();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [selectedPlace, handleClear]);

  const formatType = (types) => {
    if (!types || types.length === 0) return 'Business';
    const primary = types[0].replace(/_/g, ' ');
    return primary.charAt(0).toUpperCase() + primary.slice(1);
  };

  const performAutoSave = useCallback(async (tagsToSave, noteToSave, happyHourToSave) => {
    if (!auth.currentUser || !selectedPlace || !selectedPlace.placeId) return;

    const docId = `${auth.currentUser.uid}_${selectedPlace.placeId}`;
    const recRef = doc(db, 'artifacts', appId, 'public', 'data', 'recommendations', docId);

    if (tagsToSave.length === 0 && !noteToSave.trim() && !happyHourToSave.trim()) {
      await safeDeleteDoc(recRef).catch(console.error);
    } else {
      await safeSetDoc(recRef, {
        userId: auth.currentUser.uid,
        placeId: selectedPlace.placeId,
        placeName: selectedPlace.name,
        address: selectedPlace.formatted_address || null,
        lat: selectedPlace.lat,
        lng: selectedPlace.lng,
        type: 'restaurant',
        tags: tagsToSave,
        note: noteToSave.trim(),
        happyHourNote: happyHourToSave.trim(),
        weekdayDescriptions: selectedPlace.weekdayDescriptions || [],
        createdAt: serverTimestamp(),
      }, { merge: true }).catch(console.error);

      await safeSetDoc(doc(db, 'artifacts', appId, 'public', 'data', 'places', selectedPlace.placeId), {
        placeId: selectedPlace.placeId,
        name: selectedPlace.name,
        address: selectedPlace.formatted_address || null,
        lat: selectedPlace.lat,
        lng: selectedPlace.lng,
        hours: selectedPlace.weekdayDescriptions || [],
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser.uid
      }, { merge: true }).catch(console.error);
    }
    await refreshRecommendations();
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  }, [selectedPlace]);

  const performAutoSaveAlias = useCallback(async (aliasToSave) => {
    if (!auth.currentUser || !selectedPlace || !selectedPlace.placeId) return;

    const docId = `${auth.currentUser.uid}_${selectedPlace.placeId}`;
    const aliasRef = doc(db, 'artifacts', appId, 'public', 'data', 'aliases', docId);

    if (!aliasToSave.trim()) {
      await safeDeleteDoc(aliasRef).catch(console.error);
    } else {
      await safeSetDoc(aliasRef, {
        userId: auth.currentUser.uid,
        placeId: selectedPlace.placeId,
        placeName: selectedPlace.name,
        address: selectedPlace.formatted_address || null,
        lat: selectedPlace.lat,
        lng: selectedPlace.lng,
        alias: aliasToSave.trim(),
        createdAt: serverTimestamp(),
      }, { merge: true }).catch(console.error);
    }
  }, [selectedPlace]);

  const toggleTag = (tag) => {
    if (tag === 'Want To Go') {
      if (selectedTags.includes(tag)) {
        setShowWantToGoPrompt(true);
        return;
      } else {
        const newTags = [...selectedTags, tag];
        setSelectedTags(newTags);
        performAutoSave(newTags, customNote, happyHourNote);
        return;
      }
    }

    let newTags = [...selectedTags];

    if (selectedTags.includes(tag)) {
        // Just remove the tag and its children
        newTags = newTags.filter(t => t !== tag);
        const children = getChildTags(tag);
        newTags = newTags.filter(t => !children.includes(t));
    } else {
      newTags.push(tag);
      const parents = getParentTags(tag);
      if (parents) {
        parents.forEach(p => {
          if (!newTags.includes(p)) {
            newTags.push(p);
          }
        });
      }

      // Automatically expand if the added tag has children
      const children = getChildTags(tag);
      if (children.length > 0) {
        const findPathForTag = (node, targetTag, currentPath = []) => {
            for (const [key, nodeChildren] of Object.entries(node)) {
                if (key === targetTag) return [...currentPath, key];
                if (Object.keys(nodeChildren).length > 0) {
                    const res = findPathForTag(nodeChildren, targetTag, [...currentPath, key]);
                    if (res) return res;
                }
            }
            return null;
        };
        const path = findPathForTag(tagsHierarchy, tag);
        if (path) {
          setTagsExpandedPath(path);
        }
      }
    }
    setSelectedTags(newTags);
    performAutoSave(newTags, customNote, happyHourNote);
  };

  const renderTagLevel = (levelIndex) => {
    let node = tagsHierarchy;
    for (let i = 0; i < levelIndex; i++) {
        node = node[tagsExpandedPath[i]] || {};
    }
    const parentTag = tagsExpandedPath[levelIndex - 1];
    const childrenKeys = Object.keys(node);
    
    if (childrenKeys.length === 0) return null;

    return (
        <div key={`level-${levelIndex}`} className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-zinc-200 border-dashed">
            <button
                onClick={(e) => { e.stopPropagation(); toggleTag(parentTag); }}
                className={`px-2 py-1 text-[9px] font-black uppercase tracking-wider rounded border transition-colors ${
                  selectedTags.includes(parentTag)
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                    : 'bg-white text-zinc-700 border-zinc-300 hover:border-blue-400'
                }`}
            >
                Any {parentTag}
            </button>
            {childrenKeys.map(child => {
                const hasChildren = Object.keys(node[child] || {}).length > 0;
                const isExpanded = tagsExpandedPath[levelIndex] === child;
                const isSelected = selectedTags.includes(child);
                
                return (
                    <div
                        key={child}
                        className={`flex items-stretch rounded border transition-colors overflow-hidden ${
                            isSelected
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                              : isExpanded 
                                ? 'bg-zinc-800 text-white border-zinc-800'
                                : 'bg-white text-zinc-600 border-zinc-200 hover:border-blue-300'
                        }`}
                    >
                        <button
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                if (hasChildren) {
                                    if (isExpanded) setTagsExpandedPath(tagsExpandedPath.slice(0, levelIndex));
                                    else setTagsExpandedPath([...tagsExpandedPath.slice(0, levelIndex), child]);
                                } else {
                                    toggleTag(child);
                                }
                            }}
                            className={`px-2 py-1 text-[9px] font-black uppercase tracking-wider flex items-center gap-1 flex-1 ${isSelected ? 'hover:opacity-80' : 'hover:bg-black/5'}`}
                        >
                            {child}
                            {hasChildren && !isExpanded && <span className="ml-0.5 opacity-50">▾</span>}
                            {hasChildren && isExpanded && <span className="ml-0.5 opacity-50">▴</span>}
                        </button>
                        
                        {!isSelected && (
                          <button
                              onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTag(child);
                              }}
                              className={`px-1.5 border-l ${isExpanded ? 'border-zinc-700 hover:bg-zinc-700 text-zinc-300 hover:text-white' : 'border-zinc-200 hover:bg-blue-50 text-zinc-400 hover:text-blue-600'} transition-colors flex items-center justify-center font-black`}
                              title={`Add ${child}`}
                          >
                              +
                          </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
  };

  const handleNoteChange = (type, value) => {
    if (type === 'alias') {
      setAliasName(value);
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = setTimeout(() => {
        performAutoSaveAlias(value);
      }, 1000);
      return;
    }

    if (type === 'custom') setCustomNote(value);
    if (type === 'happyHour') setHappyHourNote(value);

    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave(selectedTags, type === 'custom' ? value : customNote, type === 'happyHour' ? value : happyHourNote);
    }, 1000);
  };

  return (
    <div ref={containerRef} className={`${className} flex flex-col gap-2 pointer-events-none`}>
      <div className="flex flex-col gap-2 pointer-events-auto relative">
        <div className={`relative flex flex-col transition-all ${overlayMode ? 'w-full' : 'w-64 md:w-80'}`}>
          <div className={`bg-white rounded-xl shadow-xl border border-zinc-200 overflow-hidden flex flex-col relative z-20`}>
            <div className={`flex items-center ${minimal ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (predictions.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setFocusedPredictionIndex(prev => prev < predictions.length - 1 ? prev + 1 : 0);
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setFocusedPredictionIndex(prev => prev > 0 ? prev - 1 : predictions.length - 1);
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (focusedPredictionIndex >= 0 && focusedPredictionIndex < predictions.length) {
                        handlePredictionSelect(predictions[focusedPredictionIndex]);
                      }
                    } else if (e.key === 'Escape') {
                      setPredictions([]);
                      setFocusedPredictionIndex(-1);
                    }
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    performSearch(inputValue);
                  }
                }}
                placeholder="Search places..."
                className={`w-full bg-transparent font-medium text-zinc-900 outline-none placeholder:text-zinc-400 ${minimal ? 'text-xs' : 'text-sm'}`}
              />
              {inputValue && (
                <button 
                  onClick={() => {
                    setInputValue('');
                    handleClear();
                  }} 
                  className="text-zinc-400 hover:text-zinc-600 p-1"
                >
                  <X size={14} strokeWidth={3} />
                </button>
              )}
            </div>
          </div>

          {predictions.length > 0 && (
            <div className={`absolute top-full ${dropdownAlign === 'right' ? 'right-0' : 'left-0'} mt-2 w-max min-w-full max-w-[90vw] md:max-w-[500px] max-h-60 overflow-y-auto pointer-events-auto bg-white rounded-xl shadow-2xl border border-zinc-200 z-[60]`}>
              {predictions.map((s, index) => {
                const mainText = s.placePrediction.text.mainText || s.placePrediction.text.toString();
                const secondaryText = s.placePrediction.text.secondaryText || '';
                const isFocused = index === focusedPredictionIndex;
                
                return (
                  <button
                    key={s.placePrediction.placeId}
                    onClick={() => handlePredictionSelect(s)}
                    className={`w-full text-left ${minimal ? 'px-2 py-1.5' : 'px-3 py-2'} border-b border-zinc-100 last:border-0 flex flex-col gap-0.5 transition-colors ${isFocused ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
                  >
                    <span className={`font-bold whitespace-normal break-words ${minimal ? 'text-[11px]' : 'text-xs'} ${isFocused ? 'text-blue-900' : 'text-zinc-900'}`}>{mainText}</span>
                    {secondaryText && <span className={`whitespace-normal break-words ${minimal ? 'text-[9px]' : 'text-[10px]'} ${isFocused ? 'text-blue-700' : 'text-zinc-500'}`}>{secondaryText}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selectedPlace && (
          <div className={overlayMode ? "fixed top-[145px] bottom-0 left-0 right-0 z-[1000] flex justify-center items-stretch pointer-events-auto p-4 md:p-6" : "absolute top-full left-0 mt-2 flex flex-col md:flex-row items-stretch gap-3 pointer-events-none z-50"}>
            {overlayMode && <div className="absolute inset-0 bg-transparent" onClick={handleClear} />}
            <div className={`flex flex-col md:flex-row items-stretch gap-4 ${overlayMode ? 'relative z-10 pointer-events-auto w-full md:w-auto' : 'pointer-events-none'}`}>
              {/* Left Column: Place Details */}
            <div className={`w-full ${overlayMode ? 'md:w-[450px] h-full' : 'md:w-[450px] max-h-[60vh] md:max-h-[70vh]'} bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-zinc-200/60 overflow-hidden pointer-events-auto animate-in fade-in slide-in-from-top-4 duration-300 flex flex-col`}>
              <div className="p-5 flex flex-col gap-3 overflow-y-auto custom-scrollbar">
                <div className="flex flex-col gap-1 border-b border-zinc-100 pb-3 relative">
                  {overlayMode && (
                    <button onClick={handleClear} className="absolute top-0 right-0 p-1.5 text-zinc-400 hover:text-zinc-700 bg-zinc-50 hover:bg-zinc-100 rounded-full transition-colors">
                      <X size={16} />
                    </button>
                  )}
                  <span className="text-[9px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full w-fit">
                    {formatType(selectedPlace.types)}
                  </span>
                  <div className="flex items-start gap-2 pr-8">
                    <h3 className="text-xl font-black text-zinc-900 leading-tight">
                      {selectedPlace.name}
                    </h3>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${selectedPlace.name} ${selectedPlace.formatted_address || ''}`.trim())}${selectedPlace.placeId ? `&query_place_id=${selectedPlace.placeId}` : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                          e.preventDefault();
                          window.open(e.currentTarget.href, 'nytm-maps-panel', 'width=480,height=900,left=0,top=0');
                        }
                      }}
                      className="p-1 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors shrink-0 mt-0.5"
                      title="Open in Google Maps"
                    >
                      <Map size={18} />
                    </a>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {selectedPlace.formatted_address && (
                    <div className="flex items-start gap-2 text-zinc-600">
                      <MapPin size={14} className="mt-0.5 shrink-0 text-zinc-400" />
                      <span className="text-sm font-medium leading-tight">{selectedPlace.formatted_address}</span>
                    </div>
                  )}
                  
                  {(selectedPlace.isOpenNow !== null && selectedPlace.isOpenNow !== undefined) || (selectedPlace.weekdayDescriptions && selectedPlace.weekdayDescriptions.length > 0) ? (
                    <div className="flex items-start gap-2 text-zinc-600">
                      <Clock size={14} className="mt-0.5 shrink-0 text-zinc-400" />
                      <div className="flex flex-col gap-0.5 w-full pr-2">
                        <div className="flex items-center justify-between w-full text-left pb-1">
                          <span className={`text-sm font-bold ${selectedPlace.isOpenNow ? 'text-green-600' : 'text-red-500'}`}>
                            {selectedPlace.isOpenNow ? 'Open Now' : 'Closed'}
                          </span>
                        </div>
                        {selectedPlace.weekdayDescriptions?.length > 0 && (
                          <div className="flex flex-col mt-1 gap-1 border-t border-zinc-100 pt-2 pb-1">
                            {selectedPlace.weekdayDescriptions.map((desc, i) => {
                              const parts = desc.split(': ');
                              const day = parts[0];
                              const hours = parts.slice(1).join(': ');
                              return (
                                <div key={i} className="flex justify-between text-xs font-medium text-zinc-500">
                                  <span>{day}</span>
                                  <span className="text-zinc-700 font-bold">{hours}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {selectedPlace.formatted_phone_number && (
                    <div className="flex items-center gap-2 text-zinc-600">
                      <Phone size={14} className="shrink-0 text-zinc-400" />
                      <a href={`tel:${selectedPlace.formatted_phone_number}`} className="text-sm font-bold hover:text-blue-600 hover:underline">
                        {selectedPlace.formatted_phone_number}
                      </a>
                    </div>
                  )}

                  {selectedPlace.website && (
                    <div className="flex items-center gap-2 text-zinc-600">
                      <Globe size={14} className="shrink-0 text-zinc-400" />
                      <a href={selectedPlace.website} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-600 hover:underline truncate">
                        {selectedPlace.website.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    </div>
                  )}

                  {selectedPlace.summary && (
                    <div className="flex items-start gap-2 text-zinc-600 mt-2 border-t border-zinc-100 pt-3">
                      <Info size={14} className="mt-0.5 shrink-0 text-zinc-400" />
                      <span className="text-sm font-medium leading-snug italic">{selectedPlace.summary}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Recommendation Section */}
            <div className={`w-full ${overlayMode ? 'md:w-[500px] h-full' : 'md:w-[500px] max-h-[60vh] md:max-h-[70vh]'} bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-zinc-200/60 overflow-hidden pointer-events-auto animate-in fade-in slide-in-from-top-4 duration-300 flex flex-col`}>
              <div className="p-4 flex flex-col gap-4 h-full overflow-y-auto custom-scrollbar">
                
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Recommend This Place
                  </span>
                  <div className="flex items-center gap-2">
                    {saveSuccess && <span className="text-[9px] font-bold text-green-500 flex items-center gap-1"><Check size={10} /> Saved</span>}
                    <button
                      onClick={() => toggleTag('Want To Go')}
                      className={`px-2 py-1 text-[9px] font-black uppercase tracking-wider rounded border transition-colors flex items-center gap-1 ${
                        selectedTags.includes('Want To Go')
                          ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20'
                          : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      Want To Go
                    </button>
                  </div>
                </div>

                {showWantToGoPrompt && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 my-2 animate-in fade-in zoom-in-95 duration-200">
                    <p className="text-xs font-bold text-orange-800 mb-2">Do you want to recommend this place?</p>
                    <p className="text-[10px] text-orange-700 mb-3 leading-snug">
                      You are removing the "Want To Go" tag. Have you visited it? If yes, keep its tags to recommend it. If no, we'll delete it from your database.
                    </p>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          const newTags = selectedTags.filter(t => t !== 'Want To Go');
                          setSelectedTags(newTags);
                          performAutoSave(newTags, customNote, happyHourNote);
                          setShowWantToGoPrompt(false);
                        }}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-[10px] font-bold py-1.5 rounded transition-colors"
                      >
                        Yes, Recommend
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedTags([]);
                          performAutoSave([], customNote, happyHourNote);
                          setShowWantToGoPrompt(false);
                          if (onClearSearch) onClearSearch();
                        }}
                        className="flex-1 bg-white hover:bg-zinc-100 text-zinc-600 border border-zinc-200 text-[10px] font-bold py-1.5 rounded transition-colors"
                      >
                        No, Delete It
                      </button>
                      <button 
                        onClick={() => setShowWantToGoPrompt(false)}
                        className="px-2 py-1.5 text-[10px] font-bold text-zinc-400 hover:text-zinc-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 flex-1 min-h-0">
                  <div className="flex items-center bg-zinc-50 rounded-lg px-2 py-1.5 border border-zinc-200 focus-within:border-blue-400 focus-within:bg-white transition-colors">
                    <Search size={12} className="text-zinc-400 mr-2 shrink-0" />
                    <input 
                      type="text"
                      placeholder="Search tags..."
                      className="bg-transparent border-none outline-none text-xs font-semibold w-full text-zinc-800 placeholder:text-zinc-400"
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                    />
                    {tagSearchQuery && (
                      <button onClick={() => setTagSearchQuery('')} className="p-0.5 text-zinc-400 hover:text-zinc-600">
                        <X size={12} strokeWidth={3} />
                      </button>
                    )}
                  </div>

                  {tagSearchQuery ? (
                    <div className="flex flex-col overflow-y-auto custom-scrollbar min-h-0">
                      <div className="flex flex-wrap gap-1.5 p-1">
                        {availableTags
                          .filter(t => t !== 'Want To Go' && t.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                          .map(tag => {
                            const parents = getParentTags(tag);
                            const parentName = parents.length > 0 ? parents[parents.length - 1] : null;
                            const displayText = parentName ? `${tag} (${parentName})` : tag;
                            return (
                              <button
                                key={tag}
                                onClick={(e) => { e.stopPropagation(); toggleTag(tag); }}
                                className={`px-2 py-1 text-[9px] font-black uppercase tracking-wider rounded border transition-colors flex items-center gap-1 ${
                                  selectedTags.includes(tag)
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-600/20'
                                    : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-blue-300 hover:bg-blue-50'
                                }`}
                              >
                                {displayText}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col overflow-y-auto custom-scrollbar min-h-0 gap-2 pr-1">
                      {/* Selected Tags Display */}
                      {selectedTags.filter(t => t !== 'Want To Go').length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-1 bg-blue-50/50 p-2 rounded-lg border border-blue-100">
                          {selectedTags.filter(t => t !== 'Want To Go').map(tag => (
                            <button
                              key={tag}
                              onClick={(e) => { e.stopPropagation(); toggleTag(tag); }}
                              className="px-2 py-1 text-[9px] font-black uppercase tracking-wider rounded border transition-colors flex items-center gap-1 bg-blue-600 text-white border-blue-600"
                            >
                              {tag} <X size={10} />
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Row 1 */}
                      <div className="flex gap-1">
                        {['Food', 'Drink', 'Coffee'].map(cat => (
                          <div key={cat} className={`flex flex-1 items-stretch rounded-lg border transition-colors overflow-hidden ${tagsExpandedPath[0] === cat ? 'bg-zinc-800 text-white border-zinc-800' : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-blue-300'}`}>
                            <button
                              onClick={() => setTagsExpandedPath(tagsExpandedPath[0] === cat ? [] : [cat])}
                              className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest ${tagsExpandedPath[0] === cat ? 'hover:bg-zinc-700' : 'hover:bg-white'} transition-colors`}
                            >
                              {cat}
                            </button>
                            {!selectedTags.includes(cat) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleTag(cat); }}
                                className={`px-2 border-l ${tagsExpandedPath[0] === cat ? 'border-zinc-700 hover:bg-zinc-700 text-zinc-300 hover:text-white' : 'border-zinc-200 hover:bg-white text-zinc-400 hover:text-blue-600'} font-black flex items-center justify-center transition-colors`}
                                title={`Add ${cat}`}
                              >
                                +
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Row 2 */}
                      <div className="flex gap-1 mt-1">
                        {['Sight', 'Music Venue', 'Shopping'].map(cat => (
                          <div key={cat} className={`flex flex-1 items-stretch rounded-lg border transition-colors overflow-hidden ${tagsExpandedPath[0] === cat ? 'bg-zinc-800 text-white border-zinc-800' : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-blue-300'}`}>
                            <button
                              onClick={() => setTagsExpandedPath(tagsExpandedPath[0] === cat ? [] : [cat])}
                              className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest ${tagsExpandedPath[0] === cat ? 'hover:bg-zinc-700' : 'hover:bg-white'} transition-colors`}
                            >
                              {cat}
                            </button>
                            {!selectedTags.includes(cat) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleTag(cat); }}
                                className={`px-2 border-l ${tagsExpandedPath[0] === cat ? 'border-zinc-700 hover:bg-zinc-700 text-zinc-300 hover:text-white' : 'border-zinc-200 hover:bg-white text-zinc-400 hover:text-blue-600'} font-black flex items-center justify-center transition-colors`}
                                title={`Add ${cat}`}
                              >
                                +
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Expanded Tag Levels */}
                      {tagsExpandedPath.length > 0 && (
                        <div className="flex flex-col p-2 bg-zinc-50 border border-zinc-200 rounded-lg shadow-inner mt-2">
                          {tagsExpandedPath.map((_, idx) => renderTagLevel(idx + 1))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Happy Hour Note
                  </span>
                  <textarea
                    value={happyHourNote}
                    onChange={(e) => handleNoteChange('happyHour', e.target.value)}
                    placeholder="e.g. $5 Margaritas 4pm-6pm..."
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-xs text-zinc-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none min-h-[40px]"
                  />
                </div>

                <div className="flex flex-col gap-2 border-t border-zinc-100 pt-2 pb-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Personal Note
                  </span>
                  <textarea
                    value={customNote}
                    onChange={(e) => handleNoteChange('custom', e.target.value)}
                    placeholder="Add your thoughts or why you want to go here..."
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-xs text-zinc-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none min-h-[60px]"
                  />
                </div>

                <div className="flex flex-col gap-2 border-t border-zinc-100 pt-2 pb-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Alias
                  </span>
                  <input
                    type="text"
                    value={aliasName}
                    onChange={(e) => handleNoteChange('alias', e.target.value)}
                    placeholder="Private alias for this place (e.g. 'My House')"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-xs text-zinc-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapSearchBox;
